import { all, call, put, takeEvery } from "redux-saga/effects"
import { withCallback } from "redux-saga-callback"

import client from "@api/client"
import { INumericIdentifierModel } from "@api/schema"
import { IFileProposalAttachment as IProposalAttachment } from "@api/schema/challenges"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import { newSingleEntityUsecaseRequestRunningAction, newSingleEntityUsecaseRequestSuccessAction } from "@redux/common/entityRequest/actions"
import { showErrorsInTestEnvironment } from "@redux/common/sagaErrorHelpers"
import { createModelSuccessAction } from "@redux/common/scopedObject/actions"
import { ProposalAttachmentHelper } from "@services/proposalAttachmentHelper"
import { SubmissionError } from "@services/submissionError"

import { IProposalAttachmentFromFileAction, IProposalAttachmentFromTeamUploadAction, ProposalAttachmentUsecases, usecaseKeyForProposalAttachmentFromFile, usecaseKeyForProposalAttachmentFromTeamUpload } from "./definitions"


export function* proposalAttachmentWatcherSaga(): any {
  yield all([
    takeEvery(ProposalAttachmentUsecases.ProposalAttachmentFromTeamUpload, withCallback(createProposalAttachmentFromTeamUploadSaga)),
    takeEvery(ProposalAttachmentUsecases.DocumentOrProjectPlanProposalAttachmentFromFile, withCallback(createDocumentAndProjectPlanProposalAttachmentFromFileSaga)),
  ])
}

/**
 * Saga to handle a creating proposal attachment from team upload action.
 */
export function* createProposalAttachmentFromTeamUploadSaga(action: IProposalAttachmentFromTeamUploadAction): Generator<any, INumericIdentifierModel, any> {

  const { onSuccess, setErrors, setSubmitting } = action.callbackActions || {}

  /**
   * Usecase key for this operation.
   */
  const usecaseKey = usecaseKeyForProposalAttachmentFromTeamUpload(action.teamUpload)

  const proposalAttachmentType = ProposalAttachmentHelper.getProposalAttachmentTypeFromAttachmentDefinitionType(action.definition.type)
  const entityType = ProposalAttachmentHelper.getEntityTypeFromProposalAttachmentType(proposalAttachmentType)

  try {
    yield put(newSingleEntityUsecaseRequestRunningAction(entityType, usecaseKey))

    const result: IProposalAttachment = yield call(client.createProposalAttachmentFromTeamUpload,
      entityType,
      {
        definition: action.definition["@id"],
        proposal: action.proposal["@id"],
        teamUpload: action.teamUpload["@id"]
      }
    )

    // signal to the use-case triggering application component: the creation was successfull
    yield put(createModelSuccessAction(entityType, result))
    yield put(newSingleEntityUsecaseRequestSuccessAction(entityType, usecaseKey, result))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
    if (onSuccess) {
      yield call(onSuccess, result, action.teamUpload)
    }

    return result
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("createProposalAttachmentFromTeamUploadSaga", errorMessage, action, err)

    if (setErrors) {
      if (err instanceof SubmissionError) {
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    // if an error occurred: signalize that the currentScopeType-request has failed with the error message
    yield put(newSingleEntityUsecaseRequestRunningAction(entityType, usecaseKey, errorMessage))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}

/**
 * Saga to handle a creating document/project plan proposal attachment from file action.
 */
export function* createDocumentAndProjectPlanProposalAttachmentFromFileSaga(action: IProposalAttachmentFromFileAction): Generator<any, INumericIdentifierModel, any> {

  const { onSuccess, setErrors, setSubmitting } = action.callbackActions || {}

  /**
   * Usecase key for this operation.
   */
  const usecaseKey = usecaseKeyForProposalAttachmentFromFile(action.definition, action.proposal)

  try {
    yield put(newSingleEntityUsecaseRequestRunningAction(action.entityType, usecaseKey))

    const result: IProposalAttachment = yield call(client.createProposalAttachmentFromFile,
      action.entityType,
      {
        definition: action.definition["@id"],
        proposal: action.proposal["@id"],
        file: action.file
      }
    )

    // signal to the use-case triggering application component: the creation was successfull
    yield put(createModelSuccessAction(action.entityType, result))
    yield put(newSingleEntityUsecaseRequestSuccessAction(action.entityType, usecaseKey, result))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
    if (onSuccess) {
      yield call(onSuccess, result)
    }

    return result
  } catch (err) {

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("createProposalAttachmentFromFileSaga", errorMessage, action, err)

    if (setErrors) {
      if (err instanceof SubmissionError) {
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: errorMessage })
      }
    }

    // if an error occurred: signalize that the currentScopeType-request has failed with the error message
    yield put(newSingleEntityUsecaseRequestRunningAction(action.entityType, usecaseKey, errorMessage))

    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return null
  }
}